// Authentic coffee recipes with proper barista sequences including grinding and tamping
export const coffeeRecipes = {
  // === ESPRESSO BASED ===
  espresso: {
    name: 'Espresso',
    category: 'espresso',
    difficulty: 1,
    sequence: ['espresso'], // Grinding and tamping handled in FirstPersonView
    description: 'Pure espresso shot - the foundation of all coffee',
    timing: [2000], // 2 seconds for espresso
    tips: 'A perfect espresso should have a golden crema on top',
    brewingSteps: ['grind_beans', 'tamp_grounds', 'extract_espresso']
  },

  doppio: {
    name: 'Doppio',
    category: 'espresso',
    difficulty: 1,
    sequence: ['espresso', 'espresso'],
    description: 'Double espresso shot for extra strength',
    timing: [2000, 2000],
    tips: 'Two shots pulled simultaneously for consistency',
    brewingSteps: ['grind_beans', 'tamp_grounds', 'extract_double_espresso']
  },

  americano: {
    name: 'Americano',
    category: 'espresso',
    difficulty: 2,
    sequence: ['espresso', 'hot_water'],
    description: 'Espresso diluted with hot water',
    timing: [2000, 1000],
    tips: 'Add hot water after espresso to preserve crema',
    brewingSteps: ['grind_beans', 'tamp_grounds', 'extract_espresso']
  },

  // === MILK BASED ===
  macchiato: {
    name: 'Macchiato',
    category: 'milk',
    difficulty: 2,
    sequence: ['espresso', 'milk_foam'],
    description: 'Espresso "marked" with a dollop of foam',
    timing: [2000, 1500],
    tips: 'Just a small dollop of foam, not steamed milk',
    brewingSteps: ['grind_beans', 'tamp_grounds', 'extract_espresso']
  },

  cortado: {
    name: 'Cortado',
    category: 'milk',
    difficulty: 3,
    sequence: ['espresso', 'steamed_milk'],
    description: 'Equal parts espresso and warm milk',
    timing: [2000, 2000],
    tips: 'Milk should be warm and velvety, not hot',
    brewingSteps: ['grind_beans', 'tamp_grounds', 'extract_espresso']
  },

  cappuccino: {
    name: 'Cappuccino',
    category: 'milk',
    difficulty: 3,
    sequence: ['espresso', 'steamed_milk', 'milk_foam'],
    description: 'Equal parts espresso, steamed milk, and foam',
    timing: [2000, 2500, 1500],
    tips: 'The foam should be thick and creamy, 1/3 of the drink',
    brewingSteps: ['grind_beans', 'tamp_grounds', 'extract_espresso']
  },

  flat_white: {
    name: 'Flat White',
    category: 'milk',
    difficulty: 4,
    sequence: ['espresso', 'microfoam_milk'],
    description: 'Espresso with velvety steamed milk and microfoam',
    timing: [2000, 3000],
    tips: 'Milk should have microfoam - creamy texture, not thick foam',
    brewingSteps: ['grind_beans', 'tamp_grounds', 'extract_espresso']
  },

  latte: {
    name: 'Latte',
    category: 'milk',
    difficulty: 3,
    sequence: ['espresso', 'steamed_milk', 'light_foam'],
    description: 'Espresso with lots of steamed milk and light foam',
    timing: [2000, 3000, 1000],
    tips: 'More milk than cappuccino, with just a thin layer of foam',
    brewingSteps: ['grind_beans', 'tamp_grounds', 'extract_espresso']
  },

  // === SPECIALTY ===
  mocha: {
    name: 'Mocha',
    category: 'specialty',
    difficulty: 4,
    sequence: ['chocolate_syrup', 'espresso', 'steamed_milk', 'whipped_cream'],
    description: 'Chocolate coffee drink with whipped cream',
    timing: [1000, 2000, 3000, 1500],
    tips: 'Mix chocolate syrup with espresso before adding milk',
    brewingSteps: ['grind_beans', 'tamp_grounds', 'extract_espresso']
  },

  affogato: {
    name: 'Affogato',
    category: 'specialty',
    difficulty: 2,
    sequence: ['vanilla_ice_cream', 'espresso'],
    description: 'Espresso poured over vanilla ice cream',
    timing: [1000, 2000],
    tips: 'Pour hot espresso over cold ice cream for contrast',
    brewingSteps: ['grind_beans', 'tamp_grounds', 'extract_espresso']
  },

  // === ICED DRINKS ===
  iced_americano: {
    name: 'Iced Americano',
    category: 'iced',
    difficulty: 2,
    sequence: ['ice', 'espresso', 'cold_water'],
    description: 'Espresso with cold water over ice',
    timing: [500, 2000, 1000],
    tips: 'Ice first to prevent melting, then espresso, then water',
    brewingSteps: ['grind_beans', 'tamp_grounds', 'extract_espresso']
  },

  iced_latte: {
    name: 'Iced Latte',
    category: 'iced',
    difficulty: 3,
    sequence: ['ice', 'espresso', 'cold_milk'],
    description: 'Espresso with cold milk over ice',
    timing: [500, 2000, 2000],
    tips: 'Pour espresso over ice first, then add cold milk slowly',
    brewingSteps: ['grind_beans', 'tamp_grounds', 'extract_espresso']
  },

  // === MATCHA DRINKS ===
  matcha_shot: {
    name: 'Matcha Shot',
    category: 'matcha',
    difficulty: 2,
    sequence: ['matcha_powder', 'hot_water', 'whisk'],
    description: 'Pure whisked matcha - concentrated and traditional',
    timing: [1000, 1500, 2000],
    tips: 'Whisk in a W pattern until frothy',
    brewingSteps: ['measure_matcha', 'whisk_preparation']
  },

  matcha_americano: {
    name: 'Matcha Americano',
    category: 'matcha',
    difficulty: 2,
    sequence: ['matcha_powder', 'hot_water', 'whisk', 'more_hot_water'],
    description: 'Matcha diluted with more hot water',
    timing: [1000, 1000, 2000, 1500],
    tips: 'Whisk concentrated matcha first, then dilute',
    brewingSteps: ['measure_matcha', 'whisk_preparation']
  },

  matcha_latte: {
    name: 'Matcha Latte',
    category: 'matcha',
    difficulty: 3,
    sequence: ['matcha_powder', 'hot_water', 'whisk', 'steamed_milk'],
    description: 'Whisked matcha with creamy steamed milk',
    timing: [1000, 1000, 2000, 3000],
    tips: 'Create smooth matcha base before adding milk',
    brewingSteps: ['measure_matcha', 'whisk_preparation']
  },

  iced_matcha_latte: {
    name: 'Iced Matcha Latte',
    category: 'matcha',
    difficulty: 3,
    sequence: ['ice', 'matcha_powder', 'cold_water', 'whisk', 'cold_milk'],
    description: 'Refreshing iced matcha with cold milk',
    timing: [500, 1000, 1000, 2000, 2000],
    tips: 'Whisk matcha with small amount of water first',
    brewingSteps: ['measure_matcha', 'whisk_preparation']
  },

  matcha_frappuccino: {
    name: 'Matcha Frappuccino',
    category: 'matcha',
    difficulty: 4,
    sequence: ['ice', 'matcha_powder', 'cold_milk', 'sweetener', 'blend', 'whipped_cream'],
    description: 'Blended iced matcha drink with whipped cream',
    timing: [500, 1000, 2000, 500, 3000, 1000],
    tips: 'Blend until smooth and creamy, top with whipped cream',
    brewingSteps: ['measure_matcha', 'blend_preparation']
  },

  dirty_matcha: {
    name: 'Dirty Matcha',
    category: 'fusion',
    difficulty: 5,
    sequence: ['matcha_powder', 'hot_water', 'whisk', 'espresso', 'steamed_milk'],
    description: 'Matcha latte with espresso shot - East meets West',
    timing: [1000, 1000, 2000, 2000, 3000],
    tips: 'Perfect balance of matcha earthiness and espresso boldness',
    brewingSteps: ['measure_matcha', 'whisk_preparation', 'grind_beans', 'tamp_grounds', 'extract_espresso']
  }
};

// Ingredient definitions with proper equipment and brewing requirements
export const ingredients = {
  // Coffee base
  espresso: {
    name: 'Espresso Shot',
    equipment: 'espresso_machine',
    type: 'coffee_base',
    requiresGrinding: true,
    requiresTamping: true,
    grindFineness: 'fine'
  },

  // Water
  hot_water: {
    name: 'Hot Water',
    equipment: 'hot_water_tap',
    type: 'liquid'
  },
  cold_water: {
    name: 'Cold Water',
    equipment: 'cold_water_tap',
    type: 'liquid'
  },

  // Milk preparations
  steamed_milk: {
    name: 'Steamed Milk',
    equipment: 'milk_steamer',
    type: 'milk',
    steamTemp: 150 // Fahrenheit
  },
  milk_foam: {
    name: 'Milk Foam',
    equipment: 'milk_steamer',
    type: 'milk',
    foamThickness: 'thick'
  },
  microfoam_milk: {
    name: 'Microfoam Milk',
    equipment: 'milk_steamer',
    type: 'milk',
    foamThickness: 'micro'
  },
  light_foam: {
    name: 'Light Foam',
    equipment: 'milk_steamer',
    type: 'milk',
    foamThickness: 'light'
  },
  cold_milk: {
    name: 'Cold Milk',
    equipment: 'refrigerator',
    type: 'milk'
  },

  // Matcha
  matcha_powder: {
    name: 'Matcha Powder',
    equipment: 'matcha_container',
    type: 'tea_base',
    requiresWhisking: true,
    whiskPattern: 'W-shape'
  },
  whisk: {
    name: 'Whisk Matcha',
    equipment: 'bamboo_whisk',
    type: 'technique',
    duration: 2000 // milliseconds
  },

  // Additions
  ice: {
    name: 'Ice Cubes',
    equipment: 'ice_machine',
    type: 'cooling'
  },
  chocolate_syrup: {
    name: 'Chocolate Syrup',
    equipment: 'syrup_dispenser',
    type: 'flavoring'
  },
  vanilla_ice_cream: {
    name: 'Vanilla Ice Cream',
    equipment: 'freezer',
    type: 'dessert'
  },
  whipped_cream: {
    name: 'Whipped Cream',
    equipment: 'whipped_cream_dispenser',
    type: 'topping'
  },
  sweetener: {
    name: 'Sweetener',
    equipment: 'sweetener_station',
    type: 'flavoring'
  },

  // Techniques
  blend: {
    name: 'Blend',
    equipment: 'blender',
    type: 'technique',
    duration: 3000 // milliseconds
  },
  more_hot_water: {
    name: 'More Hot Water',
    equipment: 'hot_water_tap',
    type: 'liquid'
  },

  // Brewing processes
  grind_beans: {
    name: 'Grind Coffee Beans',
    equipment: 'coffee_grinder',
    type: 'process',
    interactive: true
  },
  tamp_grounds: {
    name: 'Tamp Coffee Grounds',
    equipment: 'tamper',
    type: 'process',
    interactive: true,
    targetPressure: 30 // lbs
  },
  extract_espresso: {
    name: 'Extract Espresso',
    equipment: 'espresso_machine',
    type: 'process',
    interactive: true,
    targetTime: 25000, // 25 seconds
    targetVolume: 60 // ml
  }
};

// Equipment definitions with positions and capabilities
export const equipment = {
  coffee_grinder: {
    name: 'Coffee Grinder',
    position: { left: '10%', top: '15%' },
    capabilities: ['grind_adjustment', 'dose_control']
  },
  tamper: {
    name: 'Coffee Tamper',
    position: { left: '25%', top: '20%' },
    capabilities: ['pressure_control', 'level_surface']
  },
  espresso_machine: {
    name: 'Espresso Machine',
    position: { left: '15%', top: '18%' },
    capabilities: ['pressure_extraction', 'temperature_control']
  },
  milk_steamer: {
    name: 'Milk Steamer',
    position: { left: '45%', top: '16%' },
    capabilities: ['steam_pressure', 'temperature_control']
  },
  matcha_container: {
    name: 'Matcha Container',
    position: { left: '75%', top: '17%' }
  },
  bamboo_whisk: {
    name: 'Bamboo Whisk',
    position: { left: '70%', top: '25%' },
    capabilities: ['whisking_technique', 'froth_creation']
  },
  hot_water_tap: {
    name: 'Hot Water',
    position: { left: '25%', top: '12%' }
  },
  cold_water_tap: {
    name: 'Cold Water',
    position: { left: '35%', top: '12%' }
  },
  ice_machine: {
    name: 'Ice Machine',
    position: { left: '85%', top: '12%' }
  },
  blender: {
    name: 'Blender',
    position: { left: '55%', top: '12%' },
    capabilities: ['variable_speed', 'pulse_function']
  }
};